import {Link} from 'react-router-dom';
import KindTile from '../tell/KindTile';
import {kindInfo} from '../../lib/kinds';
import {LifecycleStep} from '../../lib/lifecycle';
import {formatDayMonth} from '../../lib/when';
import {Post} from '../../types';

// The plant page's vertical timeline: a rail on the left, a kind tile per step, the step's dates
// and its tells. An empty step is dim with a hint ("Last season: first on 4 Jan"), which is the
// nudge to post.

const dates = (tells: Post[]): string => {
    const days = [...new Set(tells.map(p => formatDayMonth(p.whenLocal)))];
    const place = tells.find(p => p.place)?.place?.name;
    return [days.join(', '), tells.length === 1 ? place : null].filter(Boolean).join(' · ');
};

export default function Lifecycle({steps}: {steps: LifecycleStep[]}) {
    return (
        <ol className="m-0 flex list-none flex-col p-0">
            {steps.map((step, i) => {
                const info = kindInfo(step.kind);
                const empty = step.tells.length === 0;
                const quantities = step.kind === 'harvest' ? step.tells.map(p => p.quantity).filter(Boolean) : [];
                return (
                    <li key={step.kind} className={`relative flex gap-3 pb-5 ${empty ? 'opacity-60' : ''}`}>
                        {i < steps.length - 1 && <span aria-hidden className="absolute bottom-0 left-[17px] top-9 w-[2px] bg-line" />}
                        <KindTile kind={step.kind} size={36} />
                        <div className="min-w-0 flex-1 pt-[6px]">
                            <div className="flex flex-wrap items-baseline gap-x-2">
                                <span className="font-[650]">{info.label}</span>
                                {empty ? <span className="meta italic">{step.hint}</span> : <span className="meta">{dates(step.tells)}</span>}
                            </div>
                            {quantities.length > 0 && <div className="meta mt-[2px]">{quantities.join(' · ')}</div>}
                            {!empty && (
                                <div className="rows mt-2 rounded-[14px] border-[1.5px] border-line bg-surface px-3">
                                    {step.tells.map(post => {
                                        const thumb = post.photos[0]?.thumbUrl ?? post.photos[0]?.url ?? null;
                                        return (
                                            <Link key={post.id} to={`/tell/${post.id}`} className="flex min-h-[52px] items-center gap-3 py-2 no-underline">
                                                {thumb && <img src={thumb} alt="" loading="lazy" className="h-12 w-12 shrink-0 rounded-[12px] object-cover" />}
                                                <span className="min-w-0 flex-1">
                                                    <span className="block truncate text-ink-2">{post.title || info.label}</span>
                                                    {step.tells.length > 1 && (
                                                        <span className="meta block">
                                                            {formatDayMonth(post.whenLocal)}
                                                            {post.place ? ` · ${post.place.name}` : ''}
                                                        </span>
                                                    )}
                                                </span>
                                            </Link>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </li>
                );
            })}
        </ol>
    );
}
